/**
 * Saved Projects UI Controller
 * Manages the saved projects list, folder navigation, project CRUD modals,
 * and file manager for ZIP projects. Extracted from main.js for maintainability.
 * @license GPL-3.0-or-later
 */

import { stateManager } from './state.js';
import { escapeHtml, setupNotesCounter } from './html-utils.js';
import { formatFileSize } from './download.js';
import { openModal, closeModal } from './modal-manager.js';
import { showPromptDialog } from './dialogs.js';

// The delete dialog's title, exported so the terminology suite can
// pin the exact string at the module boundary.
export const DELETE_CONFIRM_TITLE = 'Delete Saved Project';
import {
  buildNestedTree,
  getNodeAtPath,
  countFilesRecursive,
} from './zip-handler.js';
import {
  listSavedProjects,
  saveProject,
  getProject,
  touchProject,
  updateProject,
  deleteProject,
  getFolder,
  listFolders,
  renameFolder,
  deleteFolder,
  getFolderTree,
  getFolderBreadcrumbs,
  moveProject,
  getProjectsInFolder,
} from './saved-projects-manager.js';
import { getUIModeController } from './ui-mode-controller.js';
import { showErrorModal, showErrorToast } from './error-translator.js';

/**
 * Initialize the saved projects UI controller.
 * @param {Object} deps
 * @param {Function} deps.showConfirmDialog - Accessible confirmation dialog
 * @param {Function} deps.showProcessingOverlay - Processing overlay (returns dismiss function)
 * @param {Function} deps.handleFile - File loading pipeline
 * @param {Function} deps.updateStatus - Status bar message callback
 * @param {Function} deps.updateCompanionSaveButton - Companion save button state updater
 * @param {Function} deps.downloadSingleProject - Single project ZIP download
 * @param {Function} deps.setCurrentSavedProjectId - Setter for the shared currentSavedProjectId
 * @param {Function} [deps.readLinkedFolder] - Read a folder-link project's
 *   contents from disk; returns {ok, content, projectFiles, mainFilePath,
 *   fileName, finishConnect} or {ok:false, reason, message}
 * @returns {Object} Controller API
 */
/**
 * A saved project card's buttons. Each name carries the project: a screen
 * reader moving by buttons once heard "Load, Edit, Delete" card after card
 * with no way to tell whose.
 * @param {{id: string, name: string}} project
 * @param {{isZip: boolean, isFolderLink: boolean}} kind
 * @returns {string} HTML
 */
export function projectCardActionsHTML(project, { isZip, isFolderLink }) {
  const name = escapeHtml(project.name);
  return `
        <div class="saved-project-actions">
            <button class="btn btn-primary btn-load-project" data-project-id="${project.id}" aria-label="Load ${name}">
              Load
            </button>
            ${
              isZip
                ? `<button class="btn btn-secondary btn-manage-files" data-project-id="${project.id}" aria-label="Files for ${name}" title="View and manage the project's files">
              Files
            </button>`
                : ''
            }
            <button class="btn btn-secondary btn-edit-project" data-project-id="${project.id}" aria-label="Edit ${name}">
              Edit
            </button>
            ${
              isFolderLink
                ? ''
                : `<button class="btn btn-secondary btn-download-project" data-project-id="${project.id}" aria-label="Export ${name}" title="Download the project as a ZIP">
              Export
            </button>`
            }
            <button class="btn btn-danger btn-delete-project" data-project-id="${project.id}" aria-label="Delete ${name}">
              Delete
            </button>
          </div>`;
}

export function initSavedProjectsUI({
  showConfirmDialog,
  showProcessingOverlay,
  handleFile,
  updateStatus,
  updateCompanionSaveButton,
  downloadSingleProject,
  setCurrentSavedProjectId,
  readLinkedFolder = null,
}) {
  let currentFolderId = null;

  /**
   * Format relative time (e.g., "2 days ago")
   * @param {number} timestamp - Unix timestamp in milliseconds
   * @returns {string}
   */
  function formatRelativeTime(timestamp) {
    const now = Date.now();
    const diff = now - timestamp;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (seconds < 60) return 'just now';
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
    if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
    return `${years} year${years !== 1 ? 's' : ''} ago`;
  }

  /**
   * Linkify URLs in text (convert http/https URLs to clickable links)
   * @param {string} text - Plain text with URLs
   * @returns {string} - HTML string with links
   */
  function linkifyText(text) {
    if (!text) return '';

    const escaped = escapeHtml(text);
    const urlPattern = /(https?:\/\/[^\s]+)/g;

    return escaped.replace(urlPattern, (url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });
  }

  /**
   * Every path that changes the stored project set ends in a re-render, so
   * this is where other welcome-screen surfaces learn about it — the
   * linked-folders list has to drop a row when a folder-link
   * card is deleted, which also clears that folder's stored handle.
   */
  function announceProjectsRendered() {
    document.dispatchEvent(new CustomEvent('saved-projects-rendered'));
  }

  /**
   * A project's control in the list as drawn now. Saving an edit or deleting
   * draws every card again, so the button that was pressed is gone. Matched
   * on dataset rather than a selector, so no id can break the lookup.
   */
  function findCardControl(projectId, selector) {
    const card = [...document.querySelectorAll('.saved-project-card')].find(
      (el) => el.dataset.projectId === projectId
    );
    return card?.querySelector(selector) || null;
  }

  /** A folder's row in the tree as drawn now. */
  function findFolderRow(folderId) {
    if (!folderId) return null;
    const item = [...document.querySelectorAll('.folder-item')].find(
      (el) => el.dataset.folderId === folderId
    );
    return item?.querySelector('.folder-header') || null;
  }

  /**
   * Focus once a deleted project's card is gone: the card now in its place,
   * else the one before it, else its folder's row, else the note that
   * nothing is saved, else the section's heading. Never the page body.
   * @param {number} index - Where the card stood in the list, or -1
   * @param {string|null} folderId - The folder it was in
   */
  function focusAfterDelete(index, folderId) {
    const cards = document.querySelectorAll(
      '#savedProjectsList .saved-project-card'
    );
    const empty = document.getElementById('savedProjectsEmpty');
    const target =
      (index >= 0 && (cards[index] || cards[index - 1])) ||
      findFolderRow(folderId) ||
      (empty && !empty.classList.contains('hidden') ? empty : null) ||
      document.getElementById('saved-projects-heading');
    if (!target) return;
    if (!target.matches('button, [tabindex]')) target.tabIndex = -1;
    target.focus();
  }

  /**
   * Render the saved projects list on the welcome screen, with its folder
   * tree.
   */
  async function renderSavedProjectsList() {
    const savedProjectsList = document.getElementById('savedProjectsList');
    const savedProjectsEmpty = document.getElementById('savedProjectsEmpty');
    const folderTree = document.getElementById('folderTree');
    const breadcrumbNav = document.getElementById('folderBreadcrumbs');
    const breadcrumbList = document.getElementById('breadcrumbList');

    if (!savedProjectsList || !savedProjectsEmpty) return;

    // Get folder tree structure
    let folders, rootProjects, allProjects;
    try {
      const treeResult = await getFolderTree();
      folders = treeResult.folders;
      rootProjects = treeResult.rootProjects;
      allProjects = await listSavedProjects();
    } catch (error) {
      console.error('[Saved Projects] Error rendering list:', error);
      return;
    }

    // Determine what to show based on current folder
    let projectsToShow = [];
    let foldersToShow = [];

    if (currentFolderId) {
      // Show contents of current folder
      projectsToShow = allProjects.filter(
        (p) => p.folderId === currentFolderId
      );
      foldersToShow = folders.filter((f) => f.parentId === currentFolderId);

      // Also get nested folders
      const findNestedFolders = (parentId) => {
        const nested = [];
        for (const folder of folders) {
          if (folder.parentId === parentId) {
            nested.push(folder);
          }
        }
        return nested;
      };
      foldersToShow = findNestedFolders(currentFolderId);

      // Update breadcrumbs
      if (breadcrumbNav && breadcrumbList) {
        const breadcrumbs = await getFolderBreadcrumbs(currentFolderId);
        breadcrumbNav.classList.remove('hidden');
        breadcrumbList.innerHTML = `
          <li class="breadcrumb-item">
            <button class="breadcrumb-link" data-folder-id="" aria-label="Go to root">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
              Root
            </button>
          </li>
          ${breadcrumbs
            .map(
              (folder, index) => `
            <li class="breadcrumb-item">
              <button class="breadcrumb-link ${index === breadcrumbs.length - 1 ? 'current' : ''}" 
                      data-folder-id="${folder.id}"
                      ${index === breadcrumbs.length - 1 ? 'aria-current="page"' : ''}>
                ${escapeHtml(folder.name)}
              </button>
            </li>
          `
            )
            .join('')}
        `;

        // Wire up breadcrumb navigation
        breadcrumbList.querySelectorAll('.breadcrumb-link').forEach((link) => {
          link.addEventListener('click', () => {
            const folderId = link.dataset.folderId || null;
            navigateToFolder(folderId);
          });
        });
      }
    } else {
      // Show root level
      projectsToShow = rootProjects;
      foldersToShow = folders.filter((f) => !f.parentId);
      if (breadcrumbNav) {
        breadcrumbNav.classList.add('hidden');
      }
    }

    // Check if empty
    if (allProjects.length === 0 && folders.length === 0) {
      savedProjectsList.innerHTML = '';
      if (folderTree) folderTree.innerHTML = '';
      savedProjectsEmpty.classList.remove('hidden');
      announceProjectsRendered();
      return;
    }

    savedProjectsEmpty.classList.add('hidden');

    // Render folder tree (if at root level)
    if (folderTree) {
      if (currentFolderId === null) {
        folderTree.innerHTML = renderFolders(
          foldersToShow,
          folders,
          allProjects
        );
        wireUpFolderEvents(folderTree);
      } else {
        // When inside a folder, show subfolders inline
        folderTree.innerHTML = renderFolders(
          foldersToShow,
          folders,
          allProjects
        );
        wireUpFolderEvents(folderTree);
      }
    }

    // Render project cards
    const cardsHtml = projectsToShow
      .map((project) => renderProjectCard(project))
      .join('');

    savedProjectsList.innerHTML = cardsHtml;
    wireUpProjectCardEvents(savedProjectsList);
    announceProjectsRendered();
  }

  /**
   * Render folders recursively
   */
  function renderFolders(foldersToRender, allFolders, allProjects) {
    return foldersToRender
      .map((folder) => {
        const childCount = allProjects.filter(
          (p) => p.folderId === folder.id
        ).length;
        const childFolders = allFolders.filter((f) => f.parentId === folder.id);
        const totalItems = childCount + childFolders.length;
        const colorDot = folder.color
          ? `<span class="folder-color-dot" style="background: ${folder.color}"></span>`
          : '';

        return `
        <div class="folder-item" data-folder-id="${folder.id}">
          <div class="folder-header" 
               role="treeitem" 
               tabindex="0"
               aria-expanded="false"
               aria-label="${escapeHtml(folder.name)} folder, ${totalItems} items">
            <svg class="folder-expand-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
            ${colorDot}
            <svg class="folder-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
            </svg>
            <span class="folder-name">${escapeHtml(folder.name)}</span>
            <span class="folder-count">${totalItems}</span>
            <div class="folder-actions">
              <button class="btn btn-sm btn-icon btn-rename-folder" data-folder-id="${folder.id}" aria-label="Rename folder ${escapeHtml(folder.name)}" title="Rename">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </button>
              <button class="btn btn-sm btn-icon btn-delete-folder" data-folder-id="${folder.id}" aria-label="Delete folder ${escapeHtml(folder.name)}" title="Delete">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            </div>
          </div>
          <div class="folder-contents" id="folder-contents-${folder.id}">
            <!-- Contents loaded on expand -->
          </div>
        </div>
      `;
      })
      .join('');
  }

  /**
   * Render a single project card
   */
  function renderProjectCard(project) {
    const notesPreview = project.notes
      ? `<div class="saved-project-notes-preview">${linkifyText(project.notes)}</div>`
      : '';

    const savedTime = formatRelativeTime(project.savedAt);
    const loadedTime =
      project.lastLoadedAt !== project.savedAt
        ? formatRelativeTime(project.lastLoadedAt)
        : null;

    const isZip = project.kind === 'zip';
    const isFolderLink = project.kind === 'folder-link';
    const iconPath =
      isZip || isFolderLink
        ? '<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>'
        : '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline>';
    const linkedBadge = isFolderLink
      ? '<span class="saved-project-badge">Linked folder</span>'
      : '';

    return `
      <div class="saved-project-card" role="listitem" data-project-id="${project.id}" draggable="true">
        <div class="saved-project-header">
          <svg class="saved-project-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            ${iconPath}
          </svg>
          <div class="saved-project-info">
            <h4 class="saved-project-name">${escapeHtml(project.name)}</h4>
            <div class="saved-project-meta">
              ${linkedBadge}
              <span class="saved-project-date">Saved ${savedTime}</span>
              ${loadedTime ? `<span class="saved-project-date">Opened ${loadedTime}</span>` : ''}
            </div>
          </div>
        </div>
        ${notesPreview}
        ${projectCardActionsHTML(project, { isZip, isFolderLink })}
        </div>
    `;
  }

  /**
   * Wire up event listeners for folder tree
   */
  function wireUpFolderEvents(container) {
    // Folder expand/collapse
    container.querySelectorAll('.folder-header').forEach((header) => {
      header.addEventListener('click', async (e) => {
        if (e.target.closest('.folder-actions')) return;

        const folderItem = header.closest('.folder-item');
        const folderId = folderItem.dataset.folderId;
        const contents = folderItem.querySelector('.folder-contents');
        const isExpanded = header.getAttribute('aria-expanded') === 'true';

        if (isExpanded) {
          // Collapse
          header.setAttribute('aria-expanded', 'false');
          contents.classList.remove('expanded');
        } else {
          // Expand and load contents
          header.setAttribute('aria-expanded', 'true');
          contents.classList.add('expanded');
          await loadFolderContents(folderId, contents);
        }
      });

      // Double-click to navigate into folder
      header.addEventListener('dblclick', (e) => {
        if (e.target.closest('.folder-actions')) return;
        const folderId = header.closest('.folder-item').dataset.folderId;
        navigateToFolder(folderId);
      });

      // Keyboard navigation, for the row itself and not its Rename and
      // Delete buttons
      header.addEventListener('keydown', (e) => {
        if (e.target !== header) return;
        const folderId = header.closest('.folder-item').dataset.folderId;
        if (e.key === 'Enter') {
          e.preventDefault();
          navigateToFolder(folderId);
        } else if (e.key === ' ') {
          e.preventDefault();
          header.click(); // Toggle expand
        }
      });
    });

    // Rename folder buttons
    container.querySelectorAll('.btn-rename-folder').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const folderId = btn.dataset.folderId;
        await showRenameFolderDialog(folderId);
      });
    });

    // Delete folder buttons
    container.querySelectorAll('.btn-delete-folder').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const folderId = btn.dataset.folderId;
        await handleDeleteFolder(folderId);
      });
    });
  }

  /**
   * Wire up event listeners for project cards
   */
  function wireUpProjectCardEvents(container) {
    container.querySelectorAll('.btn-load-project').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        loadSavedProject(btn.dataset.projectId);
      });
    });

    container.querySelectorAll('.btn-manage-files').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        showProjectFileManager(btn.dataset.projectId);
      });
    });

    container.querySelectorAll('.btn-edit-project').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        showEditProjectModal(btn.dataset.projectId);
      });
    });

    container.querySelectorAll('.btn-download-project').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        downloadSingleProject(btn.dataset.projectId);
      });
    });

    container.querySelectorAll('.btn-delete-project').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        deleteSavedProject(btn.dataset.projectId);
      });
    });

    // Make cards clickable to load
    container.querySelectorAll('.saved-project-card').forEach((card) => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('button')) return;
        loadSavedProject(card.dataset.projectId);
      });

      card.setAttribute('tabindex', '0');
      card.addEventListener('keydown', (e) => {
        // The card's own keys only: Enter or Space on one of its buttons is
        // that button's (it was taken here, so Edit, Files, Export and
        // Delete could not be pressed from the keyboard).
        if (e.target !== card) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          loadSavedProject(card.dataset.projectId);
        }
      });

      // Drag and drop support
      card.addEventListener('dragstart', (e) => {
        e.dataTransfer.setData('text/plain', card.dataset.projectId);
        e.dataTransfer.setData(
          'application/x-project-id',
          card.dataset.projectId
        );
        card.classList.add('dragging');
      });

      card.addEventListener('dragend', () => {
        card.classList.remove('dragging');
      });
    });
  }

  /**
   * Load folder contents dynamically
   */
  async function loadFolderContents(folderId, container) {
    const allProjects = await listSavedProjects();
    const allFolders = await listFolders();

    const projectsInFolder = allProjects.filter((p) => p.folderId === folderId);
    const subfoldersInFolder = allFolders.filter(
      (f) => f.parentId === folderId
    );

    let html = '';

    // Render subfolders
    if (subfoldersInFolder.length > 0) {
      html += `<div class="folder-tree">${renderFolders(subfoldersInFolder, allFolders, allProjects)}</div>`;
    }

    // Render projects
    if (projectsInFolder.length > 0) {
      html += `<div class="saved-projects-list">${projectsInFolder.map((p) => renderProjectCard(p)).join('')}</div>`;
    }

    if (!html) {
      html = '<p class="folder-empty">This folder is empty</p>';
    }

    container.innerHTML = html;

    // Wire up events for new content
    const subFolderTree = container.querySelector('.folder-tree');
    if (subFolderTree) {
      wireUpFolderEvents(subFolderTree);
    }

    const projectsList = container.querySelector('.saved-projects-list');
    if (projectsList) {
      wireUpProjectCardEvents(projectsList);
    }

    // Setup folder as drop target
    setupFolderDropTarget(container.closest('.folder-item'));
  }

  /**
   * Navigate to a folder
   */
  async function navigateToFolder(folderId) {
    currentFolderId = folderId;
    await renderSavedProjectsList();
  }

  /**
   * Setup folder as drop target for projects
   */
  function setupFolderDropTarget(folderItem) {
    if (!folderItem) return;

    const header = folderItem.querySelector('.folder-header');
    const folderId = folderItem.dataset.folderId;

    header.addEventListener('dragover', (e) => {
      e.preventDefault();
      folderItem.classList.add('drag-over');
    });

    header.addEventListener('dragleave', () => {
      folderItem.classList.remove('drag-over');
    });

    header.addEventListener('drop', async (e) => {
      e.preventDefault();
      folderItem.classList.remove('drag-over');

      const projectId = e.dataTransfer.getData('application/x-project-id');
      if (projectId) {
        const result = await moveProject(projectId, folderId);
        if (result.success) {
          await renderSavedProjectsList();
          stateManager.announceChange('Project moved to folder');
        } else {
          showErrorToast({ title: 'Move Failed', message: result.error });
        }
      }
    });
  }

  /**
   * Show dialog to rename a folder
   */
  async function showRenameFolderDialog(folderId) {
    const folder = await getFolder(folderId);
    if (!folder) return;

    const newName = await showPromptDialog('', {
      title: 'Rename Folder',
      label: 'Folder name',
      defaultValue: folder.name,
      confirmLabel: 'Rename',
    });
    if (newName && newName !== folder.name) {
      const result = await renameFolder(folderId, newName);
      if (result.success) {
        await renderSavedProjectsList();
        findFolderRow(folderId)?.querySelector('.btn-rename-folder')?.focus();
        stateManager.announceChange(`Folder renamed to ${newName}`);
      } else {
        showErrorToast({ title: 'Rename Failed', message: result.error });
      }
    }
  }

  /**
   * Handle folder deletion
   */
  async function handleDeleteFolder(folderId) {
    const folder = await getFolder(folderId);
    if (!folder) return;

    const projectsInFolder = await getProjectsInFolder(folderId);
    const hasContents = projectsInFolder.length > 0;

    let message = `Delete the folder "${folder.name}"?`;
    if (hasContents) {
      message = `Delete the folder "${folder.name}"?\n\nThis folder contains ${projectsInFolder.length} project(s). They will be moved to the root level.`;
    }
    const index = [...document.querySelectorAll('.folder-item')].findIndex(
      (el) => el.dataset.folderId === folderId
    );

    if (
      await showConfirmDialog(message, 'Delete Folder', 'Delete', 'Cancel', {
        destructive: true,
      })
    ) {
      const result = await deleteFolder(folderId, false); // Don't delete contents, move to root
      if (result.success) {
        if (currentFolderId === folderId) {
          currentFolderId = null; // Navigate back to root if we deleted current folder
        }
        await renderSavedProjectsList();
        // The folder now in its place, else the one before, else the list.
        const rows = document.querySelectorAll('.folder-item .folder-header');
        const row = index >= 0 ? rows[index] || rows[index - 1] : null;
        if (row) row.focus();
        else focusAfterDelete(-1, null);
        stateManager.announceChange(`Folder "${folder.name}" deleted`);
      } else {
        showErrorToast({ title: 'Delete Failed', message: result.error });
      }
    }
  }

  /**
   * Show Project File Manager modal for ZIP projects
   * @param {string} projectId - The project ID
   */
  async function showProjectFileManager(projectId) {
    const project = await getProject(projectId);
    if (!project) {
      showErrorToast({
        title: 'Not Found',
        message: 'The requested project could not be found.',
      });
      return;
    }

    if (project.kind !== 'zip' || !project.projectFiles) {
      showErrorToast({
        title: 'Not Available',
        message: 'File management is only available for ZIP projects.',
      });
      return;
    }

    // currentFiles is a plain object (path → content) kept in sync with DB
    let currentFiles =
      typeof project.projectFiles === 'string'
        ? JSON.parse(project.projectFiles)
        : { ...project.projectFiles };

    // Navigation path local to this modal closure — cleaned up when modal closes
    let fmCurrentPath = [];

    const modal = document.createElement('div');
    modal.className = 'preset-modal file-manager-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-labelledby', 'fileManagerTitle');
    modal.setAttribute('aria-modal', 'true');

    modal.innerHTML = `
      <div class="preset-modal-content">
        <div class="preset-modal-header">
          <h3 id="fileManagerTitle" class="preset-modal-title">Project Files: ${escapeHtml(project.name)}</h3>
          <button class="preset-modal-close" id="fileManagerXCloseBtn" aria-label="Close file manager">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div class="preset-modal-body">
          <div class="file-manager-toolbar">
            <button type="button" class="btn btn-sm btn-outline" id="addFileToProjectBtn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add File
            </button>
            <button type="button" class="btn btn-sm btn-outline" id="addFolderToProjectBtn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                <line x1="12" y1="11" x2="12" y2="17"></line>
                <line x1="9" y1="14" x2="15" y2="14"></line>
              </svg>
              New Folder
            </button>
            <span class="file-manager-count" id="fmFileCount"></span>
          </div>

          <div id="fmBreadcrumbs"></div>
          <div class="file-manager-tree" id="fmTree"></div>
        </div>

        <div class="preset-modal-footer">
          <button class="btn btn-secondary" id="fileManagerCloseBtn">Close</button>
          <button class="btn btn-primary" id="fileManagerLoadBtn">Load Project</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    openModal(modal);

    const dismissFileManager = () => {
      closeModal(modal);
      document.body.removeChild(modal);
    };

    /** A control in the tree as drawn now, matched on dataset, not a selector. */
    const fmControl = (className, key, value) =>
      [...modal.querySelectorAll(`#fmTree .${className}`)].find(
        (el) => el.dataset[key] === value
      ) || null;

    /** Where a control's row stands in the tree, before it is drawn again. */
    const fmRowIndex = (control) =>
      [...modal.querySelectorAll('#fmTree .file-manager-item')].indexOf(
        control.closest('.file-manager-item')
      );

    /**
     * Focus once the tree is drawn again, which replaces every row: the
     * control given, else the row now where a removed one stood, else the
     * tree itself. Never the page body while the file manager is open.
     * @param {HTMLElement|null} control
     * @param {number} [rowIndex=-1]
     */
    function focusFmTree(control, rowIndex = -1) {
      const tree = modal.querySelector('#fmTree');
      const rows = tree.querySelectorAll('.file-manager-item');
      const row = rowIndex >= 0 ? rows[rowIndex] || rows[rowIndex - 1] : null;
      const rowTarget = row?.matches('[tabindex]')
        ? row
        : row?.querySelector('button:not([disabled])');
      const target = control || rowTarget || tree;
      if (target === tree) tree.tabIndex = -1;
      target.focus();
    }

    /**
     * Focus after moving to another folder, which draws the tree again: the
     * row of the folder just left when going up, else the first row, else
     * (an empty folder) the breadcrumb that names it.
     * @param {string} [leftFolder]
     */
    function focusAfterFolderMove(leftFolder) {
      const left = leftFolder
        ? fmControl(
            'file-nav-folder-row',
            'folderEnter',
            leftFolder
          )?.querySelector('.file-nav-folder-open')
        : null;
      const hasRows = !!modal.querySelector('#fmTree .file-manager-item');
      const crumbs = modal.querySelectorAll(
        '#fmBreadcrumbs .file-nav-breadcrumb-btn'
      );
      focusFmTree(left || (hasRows ? null : crumbs[crumbs.length - 1]), 0);
    }

    // ------------------------------------------------------------------ //
    // renderFmView — re-renders breadcrumbs + tree without closing modal  //
    // ------------------------------------------------------------------ //
    function renderFmView() {
      const fileMap = new Map(Object.entries(currentFiles));
      const tree = buildNestedTree(fileMap);

      // Validate path still exists after mutations
      if (getNodeAtPath(tree, fmCurrentPath) === null) {
        fmCurrentPath = [];
      }
      const node = getNodeAtPath(tree, fmCurrentPath) || tree;

      const totalFiles = Object.keys(currentFiles).filter(
        (p) => !p.endsWith('/.folder')
      ).length;
      const countEl = modal.querySelector('#fmFileCount');
      if (countEl)
        countEl.textContent = `${totalFiles} file${totalFiles !== 1 ? 's' : ''}`;

      // --- Breadcrumbs ---
      const breadcrumbsEl = modal.querySelector('#fmBreadcrumbs');
      if (fmCurrentPath.length > 0) {
        const crumbItems = fmCurrentPath.map((seg, idx) => {
          return `<li class="file-nav-breadcrumb-item">
            <button class="file-nav-breadcrumb-btn" data-depth="${idx + 1}" aria-label="Navigate to ${escapeHtml(seg)}">${escapeHtml(seg)}</button>
          </li>`;
        });
        breadcrumbsEl.innerHTML = `
          <nav class="file-nav-breadcrumbs file-nav-breadcrumbs--modal" aria-label="Folder navigation">
            <ol class="file-nav-breadcrumb-list">
              <li class="file-nav-breadcrumb-item">
                <button class="file-nav-breadcrumb-btn file-nav-breadcrumb-home" data-depth="0" aria-label="Navigate to root">\u{1F3E0} Root</button>
              </li>
              ${crumbItems.join('')}
            </ol>
          </nav>`;
        breadcrumbsEl
          .querySelectorAll('.file-nav-breadcrumb-btn')
          .forEach((btn) => {
            btn.addEventListener('click', () => {
              const depth = parseInt(btn.dataset.depth, 10);
              const left = fmCurrentPath[depth];
              fmCurrentPath = fmCurrentPath.slice(0, depth);
              renderFmView();
              focusAfterFolderMove(left);
            });
          });
      } else {
        breadcrumbsEl.innerHTML = '';
      }

      // --- Folder rows ---
      const sortedFolders = [...node.folders.entries()].sort((a, b) =>
        a[0].localeCompare(b[0])
      );

      // Build the full path prefix for a folder at current depth
      const makeFolderFullPath = (name) => [...fmCurrentPath, name].join('/');

      const folderRowsHtml = sortedFolders
        .map(([folderName, childNode]) => {
          const count = countFilesRecursive(childNode);
          const fullPath = makeFolderFullPath(folderName);
          // The name and count are the row's own button and Rename and Delete
          // stand beside it: inside a role="button" row a screen reader's
          // browse mode reads them as the row's text and cannot press them.
          return `
          <div class="file-manager-item file-nav-folder-row" data-folder-enter="${escapeHtml(folderName)}">
            <button type="button" class="file-nav-folder-open"
                    aria-label="Open folder ${escapeHtml(folderName)}, ${count} file${count !== 1 ? 's' : ''}">
              <svg class="file-manager-item-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
              </svg>
              <span class="file-manager-item-path">${escapeHtml(folderName)}</span>
              <span class="file-manager-item-size file-nav-folder-count">${count} file${count !== 1 ? 's' : ''}</span>
            </button>
            <div class="file-manager-item-actions">
              <button class="btn btn-sm btn-icon btn-rename-project-folder" data-folder="${escapeHtml(fullPath)}" aria-label="Rename folder ${escapeHtml(folderName)}" title="Rename">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </button>
              <button class="btn btn-sm btn-icon btn-delete-project-folder" data-folder="${escapeHtml(fullPath)}" aria-label="Delete folder ${escapeHtml(folderName)}" title="Delete folder">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
              <span class="file-nav-folder-chevron" aria-hidden="true">\u203A</span>
            </div>
          </div>`;
        })
        .join('');

      // --- File rows ---
      const sortedFiles = [...node.files].sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      const fileRowsHtml = sortedFiles
        .map((file) => {
          const isMain = file.path === project.mainFilePath;
          const size = new Blob([file.content]).size;
          const iconClass = isMain ? 'main-file' : '';
          return `
          <div class="file-manager-item" data-path="${escapeHtml(file.path)}">
            <svg class="file-manager-item-icon ${iconClass}" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              ${
                isMain
                  ? '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="12" y1="18" x2="12" y2="12"></line><line x1="9" y1="15" x2="15" y2="15"></line>'
                  : '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline>'
              }
            </svg>
            <span class="file-manager-item-path">${escapeHtml(file.name)}</span>
            <span class="file-manager-item-size">${formatFileSize(size)}</span>
            <div class="file-manager-item-actions">
              ${
                isMain
                  ? '<span class="file-manager-main-badge">Main</span>'
                  : `<button class="btn btn-sm btn-icon btn-set-main-file" data-path="${escapeHtml(file.path)}" aria-label="Set as main file ${escapeHtml(file.name)}" title="Set as main">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </button>`
              }
              <button class="btn btn-sm btn-icon btn-preview-file" data-path="${escapeHtml(file.path)}" aria-label="Preview file ${escapeHtml(file.name)}" title="Preview">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              </button>
              <button class="btn btn-sm btn-icon btn-rename-project-file" data-path="${escapeHtml(file.path)}" aria-label="Rename file ${escapeHtml(file.name)}" title="Rename">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </button>
              <button class="btn btn-sm btn-icon btn-delete-project-file${isMain ? ' btn-disabled' : ''}" data-path="${escapeHtml(file.path)}" aria-label="Delete file ${escapeHtml(file.name)}" title="${isMain ? 'Cannot delete main file' : 'Delete'}"${isMain ? ' disabled' : ''}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            </div>
          </div>`;
        })
        .join('');

      const treeEl = modal.querySelector('#fmTree');
      treeEl.innerHTML =
        folderRowsHtml + fileRowsHtml ||
        '<p class="text-muted">No files in this folder</p>';

      // --- Wire folder navigation ---
      treeEl.querySelectorAll('[data-folder-enter]').forEach((row) => {
        const folderName = row.dataset.folderEnter;
        const enter = () => {
          fmCurrentPath = [...fmCurrentPath, folderName];
          renderFmView();
          focusAfterFolderMove();
        };
        // The open button, or anywhere on the row but Rename and Delete. The
        // button brings Enter and Space with it.
        row.addEventListener('click', (e) => {
          if (e.target.closest('button:not(.file-nav-folder-open)')) return;
          enter();
        });
        row.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && fmCurrentPath.length > 0) {
            e.preventDefault();
            const left = fmCurrentPath[fmCurrentPath.length - 1];
            fmCurrentPath = fmCurrentPath.slice(0, -1);
            renderFmView();
            focusAfterFolderMove(left);
          }
        });
      });

      // --- Preview file buttons ---
      treeEl.querySelectorAll('.btn-preview-file').forEach((btn) => {
        btn.addEventListener('click', () => {
          const path = btn.dataset.path;
          showFilePreviewModal(path, currentFiles[path]);
        });
      });

      // --- Set main file buttons ---
      treeEl.querySelectorAll('.btn-set-main-file').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const path = btn.dataset.path;
          project.mainFilePath = path;
          const result = await updateProject({
            id: projectId,
            mainFilePath: path,
          });
          if (result.success) {
            stateManager.announceChange(`Main file set to ${path}`);
            renderFmView();
          }
        });
      });

      // --- Rename folder buttons ---
      treeEl.querySelectorAll('.btn-rename-project-folder').forEach((btn) => {
        btn.addEventListener('click', async (e) => {
          e.stopPropagation();
          const oldFullPath = btn.dataset.folder; // e.g. "Cases/iPad 7,8,9"
          const oldName = oldFullPath.split('/').pop();
          const newName = await showPromptDialog('', {
            title: 'Rename Folder',
            label: 'Folder name',
            defaultValue: oldName,
            confirmLabel: 'Rename',
          });
          if (!newName || newName === oldName) return;
          const safeName = newName.replace(/[\\/:*?"<>|]/g, '_');
          const parentPrefix = oldFullPath.includes('/')
            ? oldFullPath.slice(0, oldFullPath.lastIndexOf('/') + 1)
            : '';
          const newFullPath = parentPrefix + safeName;
          const oldPrefix = oldFullPath + '/';
          const newPrefix = newFullPath + '/';

          const conflict = Object.keys(currentFiles).some(
            (p) => p.startsWith(newPrefix) && !p.startsWith(oldPrefix)
          );
          if (conflict) {
            showErrorToast({
              title: 'Name Conflict',
              message: `Folder "${safeName}" already exists.`,
            });
            return;
          }

          const updatedFiles = {};
          for (const [path, content] of Object.entries(currentFiles)) {
            updatedFiles[
              path.startsWith(oldPrefix)
                ? newPrefix + path.slice(oldPrefix.length)
                : path
            ] = content;
          }

          let newMainFilePath = project.mainFilePath;
          if (newMainFilePath && newMainFilePath.startsWith(oldPrefix)) {
            newMainFilePath =
              newPrefix + newMainFilePath.slice(oldPrefix.length);
          }

          const result = await updateProject({
            id: projectId,
            projectFiles: JSON.stringify(updatedFiles),
            mainFilePath: newMainFilePath,
          });

          if (result.success) {
            currentFiles = updatedFiles;
            project.mainFilePath = newMainFilePath;
            // If we renamed a folder we're currently inside, update path
            fmCurrentPath = fmCurrentPath.map((seg, idx) => {
              const prefix = fmCurrentPath.slice(0, idx + 1).join('/');
              return prefix === oldFullPath ? safeName : seg;
            });
            stateManager.announceChange(`Folder renamed to "${safeName}"`);
            renderFmView();
            focusFmTree(
              fmControl('btn-rename-project-folder', 'folder', newFullPath)
            );
          } else {
            showErrorToast({ title: 'Rename Failed', message: result.error });
          }
        });
      });

      // --- Rename file buttons ---
      treeEl.querySelectorAll('.btn-rename-project-file').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const oldPath = btn.dataset.path;
          const parts = oldPath.split('/');
          const oldName = parts[parts.length - 1];
          const folder = parts.length > 1 ? parts.slice(0, -1).join('/') : '';

          const newName = await showPromptDialog('', {
            title: 'Rename File',
            label: 'New name',
            defaultValue: oldName,
            confirmLabel: 'Rename',
          });
          if (!newName || newName === oldName) return;
          const safeName = newName.replace(/[\\/:*?"<>|]/g, '_');
          const newPath = folder ? `${folder}/${safeName}` : safeName;

          if (newPath === oldPath) return;
          if (currentFiles[newPath] !== undefined) {
            showErrorToast({
              title: 'Name Conflict',
              message: `A file named "${safeName}" already exists${folder ? ` in ${folder}` : ''}.`,
            });
            return;
          }

          const updatedFiles = {};
          for (const [path, content] of Object.entries(currentFiles)) {
            updatedFiles[path === oldPath ? newPath : path] = content;
          }

          let newMainFilePath = project.mainFilePath;
          if (newMainFilePath === oldPath) newMainFilePath = newPath;

          const result = await updateProject({
            id: projectId,
            projectFiles: JSON.stringify(updatedFiles),
            mainFilePath: newMainFilePath,
          });

          if (result.success) {
            currentFiles = updatedFiles;
            project.mainFilePath = newMainFilePath;
            stateManager.announceChange(`File renamed to "${safeName}"`);
            renderFmView();
            focusFmTree(fmControl('btn-rename-project-file', 'path', newPath));
          } else {
            showErrorToast({ title: 'Rename Failed', message: result.error });
          }
        });
      });

      // --- Delete file buttons ---
      treeEl.querySelectorAll('.btn-delete-project-file').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const filePath = btn.dataset.path;
          if (filePath === project.mainFilePath) return;

          const fileName = filePath.split('/').pop();
          const rowIndex = fmRowIndex(btn);
          const confirmed = await showConfirmDialog(
            `Delete "${fileName}"? This cannot be undone.`,
            'Delete File',
            'Delete',
            'Cancel',
            { destructive: true }
          );
          if (!confirmed) return;

          const updatedFiles = { ...currentFiles };
          delete updatedFiles[filePath];

          const result = await updateProject({
            id: projectId,
            projectFiles: JSON.stringify(updatedFiles),
          });

          if (result.success) {
            currentFiles = updatedFiles;
            stateManager.announceChange(`Deleted file "${fileName}"`);
            renderFmView();
            focusFmTree(null, rowIndex);
          } else {
            showErrorToast({ title: 'Delete Failed', message: result.error });
          }
        });
      });

      // --- Delete folder buttons ---
      treeEl.querySelectorAll('.btn-delete-project-folder').forEach((btn) => {
        btn.addEventListener('click', async (e) => {
          e.stopPropagation();
          const folderFullPath = btn.dataset.folder;
          const folderName = folderFullPath.split('/').pop();
          const folderPrefix = folderFullPath + '/';

          if (
            project.mainFilePath &&
            project.mainFilePath.startsWith(folderPrefix)
          ) {
            showErrorToast({
              title: 'Cannot Delete Folder',
              message: `Folder "${folderName}" contains the main file. Move or change the main file first.`,
            });
            return;
          }

          const fileCount = Object.keys(currentFiles).filter(
            (p) => p.startsWith(folderPrefix) && !p.endsWith('/.folder')
          ).length;
          const message =
            fileCount > 0
              ? `Delete the folder "${folderName}" and its ${fileCount} file${fileCount !== 1 ? 's' : ''}? This cannot be undone.`
              : `Delete the empty folder "${folderName}"?`;
          const rowIndex = fmRowIndex(btn);

          const confirmed = await showConfirmDialog(
            message,
            'Delete Folder',
            'Delete',
            'Cancel',
            { destructive: true }
          );
          if (!confirmed) return;

          const updatedFiles = {};
          for (const [path, content] of Object.entries(currentFiles)) {
            if (!path.startsWith(folderPrefix)) updatedFiles[path] = content;
          }

          const result = await updateProject({
            id: projectId,
            projectFiles: JSON.stringify(updatedFiles),
          });

          if (result.success) {
            currentFiles = updatedFiles;
            // If we deleted a folder we're inside, navigate up
            const deletedPathStr = folderFullPath;
            const currentPathStr = fmCurrentPath.join('/');
            if (
              currentPathStr === deletedPathStr ||
              currentPathStr.startsWith(deletedPathStr + '/')
            ) {
              fmCurrentPath = folderFullPath.includes('/')
                ? folderFullPath.split('/').slice(0, -1)
                : [];
            }
            stateManager.announceChange(`Deleted folder "${folderName}"`);
            renderFmView();
            focusFmTree(null, rowIndex);
          } else {
            showErrorToast({ title: 'Delete Failed', message: result.error });
          }
        });
      });
    }

    // Initial render
    renderFmView();

    // Wire up static modal buttons
    modal
      .querySelector('#fileManagerXCloseBtn')
      .addEventListener('click', dismissFileManager);
    modal
      .querySelector('#fileManagerCloseBtn')
      .addEventListener('click', dismissFileManager);
    modal.querySelector('#fileManagerLoadBtn').addEventListener('click', () => {
      dismissFileManager();
      loadSavedProject(projectId);
    });
    // Escape closes, unless a folder row has just used it to go up a level.
    modal.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      dismissFileManager();
    });

    // Add file button
    const addFileBtn = modal.querySelector('#addFileToProjectBtn');
    if (addFileBtn) {
      addFileBtn.addEventListener('click', () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.multiple = true;
        fileInput.accept = '.scad,.json,.svg,.png,.jpg,.jpeg,.stl,.txt';

        fileInput.addEventListener('change', async (e) => {
          const files = Array.from(e.target.files || []);
          if (files.length === 0) return;

          let addedCount = 0;
          const updatedFiles = { ...currentFiles };
          // Place new files in the currently viewed folder
          const folderPrefix =
            fmCurrentPath.length > 0 ? fmCurrentPath.join('/') + '/' : '';

          for (const file of files) {
            try {
              const ext = file.name.split('.').pop().toLowerCase();
              const isText = ['scad', 'json', 'txt', 'svg'].includes(ext);
              let content;
              if (isText) {
                content = await file.text();
              } else {
                content = await new Promise((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = () => resolve(reader.result);
                  reader.onerror = reject;
                  reader.readAsDataURL(file);
                });
              }
              updatedFiles[folderPrefix + file.name] = content;
              addedCount++;
            } catch (err) {
              console.error(
                `[FileManager] Error reading file ${file.name}:`,
                err
              );
            }
          }

          if (addedCount > 0) {
            const result = await updateProject({
              id: projectId,
              projectFiles: JSON.stringify(updatedFiles),
            });
            if (result.success) {
              currentFiles = updatedFiles;
              stateManager.announceChange(
                `Added ${addedCount} file${addedCount !== 1 ? 's' : ''} to project`
              );
              renderFmView();
            } else {
              showErrorToast({
                title: 'Add Files Failed',
                message: result.error,
              });
            }
          }
        });

        fileInput.click();
      });
    }

    // New folder button — creates folder inside currently viewed folder
    const addFolderBtn = modal.querySelector('#addFolderToProjectBtn');
    if (addFolderBtn) {
      addFolderBtn.addEventListener('click', async () => {
        const folderName = await showPromptDialog('', {
          title: 'New Folder',
          label: 'Folder name',
          confirmLabel: 'Create',
        });
        if (!folderName) return;
        const safeName = folderName.replace(/[\\/:*?"<>|]/g, '_');
        const parentPrefix =
          fmCurrentPath.length > 0 ? fmCurrentPath.join('/') + '/' : '';
        const newFolderPrefix = parentPrefix + safeName + '/';

        const exists = Object.keys(currentFiles).some((p) =>
          p.startsWith(newFolderPrefix)
        );
        if (exists) {
          showErrorToast({
            title: 'Name Conflict',
            message: `Folder "${safeName}" already exists.`,
          });
          return;
        }

        const updatedFiles = { ...currentFiles };
        updatedFiles[`${newFolderPrefix}.folder`] = '';

        const result = await updateProject({
          id: projectId,
          projectFiles: JSON.stringify(updatedFiles),
        });

        if (result.success) {
          currentFiles = updatedFiles;
          stateManager.announceChange(`Created folder "${safeName}"`);
          renderFmView();
        } else {
          showErrorToast({
            title: 'Create Folder Failed',
            message: result.error,
          });
        }
      });
    }
  }

  /**
   * Show file preview modal
   */
  function showFilePreviewModal(path, content) {
    const modal = document.createElement('div');
    modal.className = 'preset-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-labelledby', 'filePreviewTitle');
    modal.setAttribute('aria-modal', 'true');

    const ext = path.split('.').pop().toLowerCase();
    const isCode = ['scad', 'json', 'txt', 'md'].includes(ext);

    modal.innerHTML = `
      <div class="preset-modal-content">
        <div class="preset-modal-header">
          <h3 id="filePreviewTitle" class="preset-modal-title">${escapeHtml(path)}</h3>
        </div>

        <div class="preset-modal-body">
          ${
            isCode
              ? `<pre style="max-height: 400px; overflow: auto; background: var(--color-bg-secondary); padding: var(--space-md); border-radius: var(--border-radius-sm); font-size: var(--font-size-sm);"><code>${escapeHtml(content)}</code></pre>`
              : `<p class="text-muted">Binary file - preview not available</p>`
          }
        </div>

        <div class="preset-modal-footer">
          <button class="btn btn-secondary" id="filePreviewCloseBtn">Close</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    openModal(modal);

    const dismissPreview = () => {
      closeModal(modal);
      document.body.removeChild(modal);
    };

    modal
      .querySelector('#filePreviewCloseBtn')
      .addEventListener('click', dismissPreview);
    modal.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      dismissPreview();
    });
  }

  /**
   * Load a saved project
   * @param {string} projectId
   */
  /**
   * Apply per-project UI preferences from the project record (authoritative
   * source; overrides whatever handleFile loaded from the legacy
   * localStorage sidecar).
   */
  function applyProjectUiPreferences(project) {
    if (project.uiPreferences == null) return;
    try {
      getUIModeController().importPreferences(project.uiPreferences, {
        applyImmediately: true,
      });
      console.log(
        `[App] Applied per-project UI preferences from project record: ${project.name}`
      );
    } catch (prefsErr) {
      console.warn('[App] Could not apply project UI preferences:', prefsErr);
    }
  }

  /**
   * Load a folder-link project by re-reading its folder from disk.
   * Assumes the replace-confirmation already happened.
   */
  async function loadLinkedFolderProject(project) {
    if (typeof readLinkedFolder !== 'function') {
      showErrorModal({
        title: 'Folder Link Missing',
        message:
          'This project points to a folder on your computer, but folder access is not available here.',
        suggestion: 'Use Chrome or Edge to open linked-folder projects.',
      });
      return;
    }

    // No overlay before readLinkedFolder: the permission prompt needs the
    // click's user activation, and an assertive overlay would talk over it.
    const linked = await readLinkedFolder(project);
    if (!linked.ok) {
      if (linked.reason === 'no-handle') {
        showErrorModal({
          title: 'Folder Link Missing',
          message:
            'This project points to a folder on your computer, but the link is gone.',
          suggestion:
            'Click Connect Folder and choose the folder again to relink it.',
          technical: linked.message,
        });
      } else if (linked.reason === 'permission-denied') {
        showErrorModal({
          title: 'Permission Needed',
          message: 'The browser needs permission to read this folder.',
          suggestion:
            'Click Load again and choose Allow when your browser asks.',
        });
      } else {
        showErrorModal({
          title: 'Folder Read Failed',
          message: 'The linked folder could not be read.',
          suggestion: 'Check that the folder still exists, then try again.',
          technical: linked.message,
        });
      }
      return;
    }

    const dismissOverlay = showProcessingOverlay(
      `Loading "${project.name}" from your folder…`,
      { hint: 'Reading files from disk. Large projects may take a moment.' }
    );
    try {
      await new Promise((r) => setTimeout(r, 0));

      await touchProject(project.id);
      setCurrentSavedProjectId(project.id);

      await handleFile(
        { name: linked.fileName },
        linked.content,
        linked.projectFiles,
        linked.mainFilePath,
        'saved',
        project.name
      );

      applyProjectUiPreferences(project);

      if (typeof linked.finishConnect === 'function') {
        await linked.finishConnect();
      }

      dismissOverlay();
      updateCompanionSaveButton();
      stateManager.announceChange(`Loaded saved project: ${project.name}`);
      updateStatus(`Loaded: ${project.name}`);
      await renderSavedProjectsList();
    } catch (error) {
      dismissOverlay();
      console.error('Error loading linked folder project:', error);
      showErrorModal({
        title: 'Project Load Failed',
        message: 'The linked folder project could not be loaded.',
        suggestion: 'Check that the folder still exists, then try again.',
        technical: error.message,
      });
    }
  }

  async function loadSavedProject(projectId) {
    let dismissOverlay = () => {};
    try {
      const project = await getProject(projectId);
      if (!project) {
        showErrorToast({
          title: 'Not Found',
          message: 'The requested project could not be found.',
        });
        return;
      }

      // If a file is already uploaded, confirm before replacing
      const state = stateManager.getState();
      if (state.uploadedFile) {
        const confirmed = await showConfirmDialog(
          'Load this project? Your current file will be replaced.\n\nAny unsaved changes will be lost.',
          'Load Saved Project',
          'Load Project',
          'Cancel'
        );
        if (!confirmed) return;
      }

      // Pointer model: folder-link projects re-read their contents from disk.
      if (project.kind === 'folder-link') {
        await loadLinkedFolderProject(project);
        return;
      }

      dismissOverlay = showProcessingOverlay(
        `Loading "${project.name}"\u2026`,
        {
          hint: 'Large projects may take a moment to open.',
        }
      );

      // Yield to allow the browser to paint the overlay before heavy work begins
      await new Promise((r) => setTimeout(r, 0));

      // Reconstruct file data
      const content = project.content;
      const fileName = project.originalName;
      const projectFiles = project.projectFiles
        ? new Map(Object.entries(project.projectFiles))
        : null;
      const mainFilePath =
        projectFiles && projectFiles.size > 0 ? project.mainFilePath : null;

      // Update last loaded timestamp
      await touchProject(projectId);

      // Track which saved project is loaded (for companion file auto-save)
      setCurrentSavedProjectId(projectId);

      // Load the file (reuse existing handleFile logic)
      await handleFile(
        { name: fileName, sourceExampleKey: project.sourceExampleKey || null },
        content,
        projectFiles,
        mainFilePath,
        'saved',
        project.name
      );

      applyProjectUiPreferences(project);

      dismissOverlay();

      // Update companion save button after loading
      updateCompanionSaveButton();

      // Announce success
      stateManager.announceChange(`Loaded saved project: ${project.name}`);
      updateStatus(`Loaded: ${project.name}`);

      // Re-render list to update "last opened" time
      await renderSavedProjectsList();
    } catch (error) {
      dismissOverlay();
      console.error('Error loading saved project:', error);
      showErrorModal({
        title: 'Project Load Failed',
        message: 'The saved project could not be loaded.',
        suggestion:
          'The project data may be corrupted. Try deleting and re-saving it.',
        technical: error.message,
      });
    }
  }

  /**
   * Show opt-in save prompt after file upload or program entry.
   * @param {Object} fileData - Current file state
   * @param {Object} [options]
   * @param {() => HTMLElement|null} [options.focusAfter] - Where focus goes
   *   when the prompt closes, looked up on opening and again on closing. The
   *   prompt after a load opens without a press, so what had focus before it
   *   is no place to go back to.
   * @param {string} [options.title] - The dialog's title, for a File menu
   *   command that opens it (Save As, Save a Copy)
   * @param {string} [options.purpose] - One sentence saying where Save writes
   *   afterwards, read as the dialog's description
   * @returns {Promise<{saved: boolean, projectId?: string}>}
   */
  async function showSaveProjectPrompt(
    fileData,
    {
      focusAfter = null,
      title = 'Save this file for quick access?',
      purpose = '',
    } = {}
  ) {
    const { uploadedFile, projectFiles, mainFilePath, sourceExampleKey } =
      fileData;

    if (!uploadedFile) return { saved: false };

    const kind = projectFiles ? 'zip' : 'scad';
    const fileName = uploadedFile.name || 'untitled.scad';

    return new Promise((resolvePrompt) => {
      const modal = document.createElement('div');
      modal.className = 'preset-modal save-project-modal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-labelledby', 'saveProjectTitle');
      modal.setAttribute('aria-modal', 'true');
      if (purpose) modal.setAttribute('aria-describedby', 'saveProjectPurpose');

      modal.innerHTML = `
        <div class="preset-modal-content">
          <div class="preset-modal-header">
            <h3 id="saveProjectTitle" class="preset-modal-title">${escapeHtml(title)}</h3>
            <button class="preset-modal-close" aria-label="Close dialog">&times;</button>
          </div>
          <div class="modal-body">
            ${purpose ? `<p id="saveProjectPurpose" class="save-project-intro">${escapeHtml(purpose)}</p>` : ''}
            <p class="save-project-intro">
              Saved projects are stored in this browser. Clearing cache/site data will remove them.
            </p>
            <div class="edit-project-field">
              <label for="saveProjectName">Project Name</label>
              <input type="text" id="saveProjectName" value="${escapeHtml(fileName)}" />
            </div>
            <div class="save-project-notes-field">
              <label for="saveProjectNotes">Notes (optional - you can paste links)</label>
              <textarea id="saveProjectNotes" placeholder="Add notes about this project..."></textarea>
              <div class="save-project-notes-counter">
                <span id="saveProjectNotesCount">0</span> / 5000 characters
              </div>
            </div>
            <div id="saveProjectDuplicateWarning" class="save-project-duplicate-warning">
              <p class="save-project-duplicate-title">
                \u26A0 A project named &ldquo;<span id="saveProjectDuplicateName"></span>&rdquo; already exists.
              </p>
              <p class="save-project-duplicate-text">
                Do you want to overwrite it, or save this as a new copy?
              </p>
            </div>
          </div>
          <div class="preset-modal-footer" id="saveProjectFooter">
            <button class="btn btn-secondary" id="saveProjectNotNow">Not now</button>
            <button class="btn btn-primary" id="saveProjectSave">Save</button>
          </div>
        </div>
      `;

      document.body.appendChild(modal);

      const nameInput = modal.querySelector('#saveProjectName');
      const notesTextarea = modal.querySelector('#saveProjectNotes');
      const notesCount = modal.querySelector('#saveProjectNotesCount');
      const saveBtn = modal.querySelector('#saveProjectSave');
      const notNowBtn = modal.querySelector('#saveProjectNotNow');
      const closeBtn = modal.querySelector('.preset-modal-close');
      const footer = modal.querySelector('#saveProjectFooter');
      const duplicateWarning = modal.querySelector(
        '#saveProjectDuplicateWarning'
      );
      const duplicateNameSpan = modal.querySelector(
        '#saveProjectDuplicateName'
      );

      const counter = modal.querySelector('.save-project-notes-counter');
      setupNotesCounter(notesTextarea, notesCount, counter, {
        maxLength: 5000,
        warningThreshold: 4500,
        onValidChange: (isValid) => {
          saveBtn.disabled = !isValid;
        },
      });

      async function doSave(overwriteProject) {
        const projectName = nameInput.value.trim() || fileName;
        const notes = notesTextarea.value.trim();
        const projectFilesObj = projectFiles
          ? Object.fromEntries(projectFiles)
          : null;

        let result;
        let savedProjectId = null;
        if (overwriteProject) {
          result = await updateProject({
            id: overwriteProject.id,
            notes,
            content: uploadedFile.content,
            projectFiles:
              projectFilesObj !== null
                ? JSON.stringify(projectFilesObj)
                : undefined,
          });
          if (result.success) {
            savedProjectId = overwriteProject.id;
            setCurrentSavedProjectId(savedProjectId);
          }
        } else {
          result = await saveProject({
            name: projectName,
            originalName: fileName,
            kind,
            mainFilePath: mainFilePath || fileName,
            content: uploadedFile.content,
            projectFiles: projectFilesObj,
            notes,
            sourceExampleKey: sourceExampleKey || undefined,
          });
          if (result.success) {
            savedProjectId = result.id;
            setCurrentSavedProjectId(savedProjectId);
          }
        }

        if (result.success) {
          updateCompanionSaveButton();
          stateManager.announceChange(`Project saved: ${projectName}`);
          updateStatus(`Saved: ${projectName}`);
          await renderSavedProjectsList();
        } else {
          showErrorModal({
            title: 'Save Failed',
            message: 'The project could not be saved.',
            suggestion: 'Check available browser storage space and try again.',
            technical: result.error,
          });
        }

        closeModal(modal, { fallbackFocus: focusAfter?.() });
        modal.remove();
        resolvePrompt({
          saved: result.success,
          projectId: savedProjectId,
        });
      }

      saveBtn.addEventListener('click', async () => {
        const projectName = nameInput.value.trim() || fileName;
        const existingProjects = await listSavedProjects();
        const duplicate = existingProjects.find((p) => p.name === projectName);

        if (duplicate) {
          duplicateNameSpan.textContent = projectName;
          duplicateWarning.style.display = 'block';

          footer.innerHTML = `
            <button class="btn btn-secondary" id="saveProjectNewCopy">Save as New Copy</button>
            <button class="btn btn-danger" id="saveProjectOverwrite">Overwrite Existing</button>
          `;
          footer
            .querySelector('#saveProjectOverwrite')
            .addEventListener('click', () => doSave(duplicate));
          footer
            .querySelector('#saveProjectNewCopy')
            .addEventListener('click', () => doSave(null));
        } else {
          await doSave(null);
        }
      });

      const closeHandler = () => {
        closeModal(modal, { fallbackFocus: focusAfter?.() });
        modal.remove();
        resolvePrompt({ saved: false });
      };

      notNowBtn.addEventListener('click', closeHandler);
      closeBtn.addEventListener('click', closeHandler);
      modal.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        e.preventDefault();
        closeHandler();
      });

      // Straight to the name: a first stop on the close button, then the
      // name, had a screen reader say the dialog's name twice.
      openModal(modal, {
        returnFocus: focusAfter?.() || undefined,
        focusTarget: nameInput,
      });
      nameInput.select();
    });
  }

  /**
   * Show edit project modal
   * @param {string} projectId
   */
  async function showEditProjectModal(projectId) {
    try {
      const project = await getProject(projectId);
      if (!project) {
        showErrorToast({
          title: 'Not Found',
          message: 'The requested project could not be found.',
        });
        return;
      }

      // Create modal
      const modal = document.createElement('div');
      modal.className = 'preset-modal edit-project-modal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-labelledby', 'editProjectTitle');
      modal.setAttribute('aria-modal', 'true');

      modal.innerHTML = `
        <div class="preset-modal-content">
          <div class="preset-modal-header">
            <h3 id="editProjectTitle" class="preset-modal-title">Edit Project</h3>
            <button class="preset-modal-close" aria-label="Close dialog">&times;</button>
          </div>
          <div class="modal-body">
            <div class="edit-project-field">
              <label for="editProjectName">Project Name</label>
              <input type="text" id="editProjectName" value="${escapeHtml(project.name)}" />
            </div>
            <div class="edit-project-field">
              <label for="editProjectNotes">Notes</label>
              <textarea id="editProjectNotes">${escapeHtml(project.notes || '')}</textarea>
              <div class="save-project-notes-counter">
                <span id="editProjectNotesCount">${(project.notes || '').length}</span> / 5000 characters
              </div>
            </div>
          </div>
          <div class="preset-modal-footer">
            <button class="btn btn-secondary" id="editProjectCancel">Cancel</button>
            <button class="btn btn-primary" id="editProjectSave">Save Changes</button>
          </div>
        </div>
      `;

      document.body.appendChild(modal);

      // Get elements
      const nameInput = modal.querySelector('#editProjectName');
      const notesTextarea = modal.querySelector('#editProjectNotes');
      const notesCount = modal.querySelector('#editProjectNotesCount');
      const saveBtn = modal.querySelector('#editProjectSave');
      const cancelBtn = modal.querySelector('#editProjectCancel');
      const closeBtn = modal.querySelector('.preset-modal-close');

      // Setup character counter for notes with validation
      const counter = modal.querySelector('.save-project-notes-counter');
      setupNotesCounter(notesTextarea, notesCount, counter, {
        maxLength: 5000,
        warningThreshold: 4500,
        submitButton: saveBtn, // Disable save button when over limit
      });

      // Handle save
      saveBtn.addEventListener('click', async () => {
        const name = nameInput.value.trim();
        const notes = notesTextarea.value.trim();

        if (!name) {
          showErrorToast({
            title: 'Name Required',
            message: 'Project name cannot be empty.',
          });
          return;
        }

        const result = await updateProject({
          id: projectId,
          name,
          notes,
        });

        if (result.success) {
          stateManager.announceChange(`Project updated: ${name}`);
          updateStatus(`Updated: ${name}`);
          await renderSavedProjectsList();
        } else {
          showErrorToast({ title: 'Update Failed', message: result.error });
        }

        closeModal(modal, {
          fallbackFocus:
            findCardControl(projectId, '.btn-edit-project') ||
            findFolderRow(project.folderId),
        });
        modal.remove();
      });

      // Handle close
      const closeHandler = () => {
        closeModal(modal);
        modal.remove();
      };

      cancelBtn.addEventListener('click', closeHandler);
      closeBtn.addEventListener('click', closeHandler);
      modal.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        e.preventDefault();
        closeHandler();
      });

      // Open modal with focus management
      openModal(modal);
      nameInput.focus();
      nameInput.select();
    } catch (error) {
      console.error('Error showing edit modal:', error);
      showErrorModal({
        title: 'Project Load Failed',
        message: 'Could not load project details for editing.',
        technical: error.message,
      });
    }
  }

  /**
   * Delete a saved project (with confirmation)
   * @param {string} projectId
   */
  async function deleteSavedProject(projectId) {
    try {
      const project = await getProject(projectId);
      if (!project) {
        showErrorToast({
          title: 'Not Found',
          message: 'The requested project could not be found.',
        });
        return;
      }

      const confirmed = await showConfirmDialog(
        `Delete "${project.name}"?\n\nThis cannot be undone.`,
        DELETE_CONFIRM_TITLE,
        'Delete',
        'Cancel'
      );

      if (!confirmed) return;

      const index = [
        ...document.querySelectorAll('#savedProjectsList .saved-project-card'),
      ].findIndex((el) => el.dataset.projectId === projectId);

      const result = await deleteProject(projectId);

      if (result.success) {
        stateManager.announceChange(`Deleted project: ${project.name}`);
        updateStatus(`Deleted: ${project.name}`);
        await renderSavedProjectsList();
        focusAfterDelete(index, project.folderId);
      } else {
        showErrorToast({ title: 'Delete Failed', message: result.error });
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      showErrorToast({ title: 'Delete Failed', message: error.message });
    }
  }

  // ---- Public API ----
  return {
    renderSavedProjectsList,
    showSaveProjectPrompt,
    loadSavedProject,
  };
}
